import api, { API_BASE_URL } from '@/shared/api';
import { buildFormData, type ApiResponse } from '@/shared/lib';
import type { GeneratedRecordAttachment } from '../model/generated-record.types';

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data; charset=UTF-8' };

function attachmentOf(response: { data: ApiResponse<GeneratedRecordAttachment> }) {
  const attachment = response.data.data;

  if (attachment === undefined) {
    throw new Error('Resposta do servidor sem o anexo enviado.');
  }

  return attachment;
}

// The generation screen has no record yet: the file stays pending, of this person, this matrix and
// this visit of the screen, until the creation links it.
export async function uploadPendingGeneratedRecordAttachment(
  matrixId: number,
  visitId: string,
  file: File
): Promise<GeneratedRecordAttachment> {
  const response = await api.post<ApiResponse<GeneratedRecordAttachment>>(
    '/generated-records/attachments',
    buildFormData({ file, matrixId, visitId }),
    { headers: MULTIPART_HEADERS }
  );

  return attachmentOf(response);
}

export async function deletePendingGeneratedRecordAttachment(attachmentId: string): Promise<void> {
  await api.delete(`/generated-records/attachments/${attachmentId}`);
}

// Every pending file of the person in that visit of the generation screen, in one request.
export async function deletePendingGeneratedRecordVisit(visitId: string): Promise<void> {
  await api.delete('/generated-records/attachments', { params: { visitId } });
}

// Plain `fetch` with `keepalive`, as the release of the lock: only a keepalive request outlives a page
// that is going away.
export function deletePendingGeneratedRecordVisitOnUnload(visitId: string): void {
  try {
    fetch(`${API_BASE_URL}/generated-records/attachments?visitId=${encodeURIComponent(visitId)}`, {
      method: 'DELETE',
      credentials: 'include',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // An unloading page has nowhere to report a failure.
  }
}

// An Upload record has no lock, so it goes without a token; every other record needs the token of
// the lock this member holds.
export async function uploadGeneratedRecordAttachment(
  id: number,
  file: File,
  inUseToken?: string
): Promise<GeneratedRecordAttachment> {
  const response = await api.post<ApiResponse<GeneratedRecordAttachment>>(
    `/generated-records/${id}/attachments`,
    buildFormData({ file, inUseToken }),
    { headers: MULTIPART_HEADERS }
  );

  return attachmentOf(response);
}

export async function deleteGeneratedRecordAttachment(
  id: number,
  attachmentId: string,
  inUseToken?: string
): Promise<void> {
  const url = `/generated-records/${id}/attachments/${attachmentId}`;
  if (inUseToken === undefined) {
    await api.delete(url);
    return;
  }
  await api.delete(url, { data: { inUseToken } });
}

export async function getGeneratedRecordAttachmentFile(
  id: number,
  attachmentId: string
): Promise<Blob> {
  const response = await api.get<Blob>(
    `/generated-records/${id}/attachments/${attachmentId}/file`,
    { responseType: 'blob' }
  );

  return response.data;
}
